import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  ArrowUpDown,
  Box,
  ChevronRight,
  PackagePlus,
  Plus,
  ScanLine,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react'
import { useDatos } from '../store/datos'
import { useSesion } from '../store/sesion'
import { Pagina } from '../components/Layout'
import { Boton, Chip, Tarjeta, Vacio } from '../components/ui/base'
import { Hoja } from '../components/ui/Hoja'
import { EscanerCodigo } from '../components/Escaner'
import { FormularioProducto } from '../components/FormularioProducto'
import { cn } from '../lib/cn'
import { TONO_CATEGORIA } from '../lib/tonos'
import { ESTADO_STOCK_META, estadoStock, money, normalizar, num, pesoLabel } from '../lib/format'
import { resumenInventario } from '../lib/metricas'
import type { Producto } from '../types'

type Orden = 'catalogo' | 'mas_stock' | 'menos_stock' | 'nombre' | 'codigo' | 'valor'

const ORDENES: { valor: Orden; etiqueta: string }[] = [
  { valor: 'catalogo', etiqueta: 'Catálogo' },
  { valor: 'nombre', etiqueta: 'Nombre' },
  { valor: 'codigo', etiqueta: 'Código' },
  { valor: 'mas_stock', etiqueta: 'Más stock' },
  { valor: 'menos_stock', etiqueta: 'Menos stock' },
  { valor: 'valor', etiqueta: 'Mayor valor' },
]

export function Inventario() {
  const { productos, ajustes } = useDatos()
  const esAdmin = useSesion((s) => s.usuario?.rol === 'admin')
  const [params, setParams] = useSearchParams()

  const [texto, setTexto] = useState('')
  const [orden, setOrden] = useState<Orden>('catalogo')
  const [panelOrden, setPanelOrden] = useState(false)
  const [escaner, setEscaner] = useState(false)
  const [nuevo, setNuevo] = useState(false)

  const soloCriticos = params.get('filtro') === 'criticos'
  const umbral = ajustes.stockMinimoGlobal
  const resumen = useMemo(() => resumenInventario(productos, umbral), [productos, umbral])

  const lista = useMemo(() => {
    const q = normalizar(texto.trim())
    // Los descontinuados también se muestran —con una X encima—, no se
    // esconden: así el administrador los puede volver a activar desde
    // la misma lista.
    let salida = [...productos]

    if (q) {
      salida = salida.filter((p) =>
        [p.nombre, p.codigo, p.codigoBarras].some((c) => c && normalizar(c).includes(q)),
      )
    }
    if (soloCriticos) salida = salida.filter((p) => estadoStock(p, umbral) !== 'disponible')

    const comparadores: Record<Orden, (a: Producto, b: Producto) => number> = {
      // Los que tienen posición fija van primero y en ese orden; el
      // resto cae al final, ordenado por nombre.
      catalogo: (a, b) => {
        const oa = a.ordenCatalogo ?? Infinity
        const ob = b.ordenCatalogo ?? Infinity
        return oa !== ob ? oa - ob : a.nombre.localeCompare(b.nombre, 'es')
      },
      nombre: (a, b) => a.nombre.localeCompare(b.nombre, 'es'),
      codigo: (a, b) => a.codigo.localeCompare(b.codigo, 'es'),
      mas_stock: (a, b) => b.stock - a.stock,
      menos_stock: (a, b) => a.stock - b.stock,
      valor: (a, b) => b.stock * b.precioSugerido - a.stock * a.precioSugerido,
    }
    return [...salida].sort((a, b) => {
      // Los descontinuados, siempre al final, sin importar el orden elegido.
      if (a.activo !== b.activo) return a.activo ? -1 : 1
      return comparadores[orden](a, b)
    })
  }, [productos, texto, orden, soloCriticos, umbral])

  return (
    <Pagina>
      {/* Semáforo del inventario: los tres estados con su recuento. */}
      <div className="mb-4 grid grid-cols-3 gap-2.5">
        {(
          [
            ['disponible', resumen.disponibles, 'Disponibles'],
            ['poco', resumen.pocos, 'Poco stock'],
            ['agotado', resumen.agotados, 'Sin stock'],
          ] as const
        ).map(([estado, cantidad, etiqueta]) => {
          const meta = ESTADO_STOCK_META[estado]
          const activo = soloCriticos && estado !== 'disponible'
          return (
            <button
              key={estado}
              onClick={() => {
                if (estado === 'disponible') setParams({})
                else setParams(soloCriticos ? {} : { filtro: 'criticos' })
              }}
              className={cn(
                'rounded-2xl border p-3.5 text-left transition-all duration-200',
                activo ? 'border-bosque-400 superficie-elevada sombra-carta' : 'borde-suave superficie-elevada',
                'hover:sombra-carta',
              )}
            >
              <span className="mb-1.5 flex items-start gap-1.5">
                <span className={cn('mt-1 size-2 shrink-0 rounded-full', meta.punto)} />
                <span className="text-[10px] leading-tight font-bold texto-suave uppercase">
                  {etiqueta}
                </span>
              </span>
              <span className="cifra font-display text-2xl font-bold texto-base">{cantidad}</span>
            </button>
          )
        })}
      </div>

      {/* Buscador y filtros */}
      <div className="mb-4 flex gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-4 size-[18px] -translate-y-1/2 texto-tenue" />
          <input
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Buscar por nombre o código…"
            className="h-12 w-full rounded-2xl border borde-base superficie-elevada pr-11 pl-11 text-[15px] texto-base transition-colors placeholder:texto-tenue focus:border-bosque-400 focus:outline-none focus:ring-4 focus:ring-bosque-500/10"
          />
          {texto && (
            <button
              onClick={() => setTexto('')}
              className="absolute top-1/2 right-3 grid size-8 -translate-y-1/2 place-items-center rounded-lg texto-tenue hover:texto-base"
              aria-label="Borrar búsqueda"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
        <button
          onClick={() => setEscaner(true)}
          className="grid size-12 shrink-0 place-items-center rounded-2xl border borde-base superficie-elevada texto-suave transition-colors hover:superficie-hundida hover:texto-base"
          aria-label="Escanear código de barras"
        >
          <ScanLine className="size-5" />
        </button>
        <button
          onClick={() => setPanelOrden(true)}
          className="grid size-12 shrink-0 place-items-center rounded-2xl border borde-base superficie-elevada texto-suave transition-colors hover:superficie-hundida hover:texto-base"
          aria-label="Ordenar y filtrar"
        >
          <SlidersHorizontal className="size-5" />
        </button>
      </div>

      {(soloCriticos || texto) && (
        <div className="mb-4 flex justify-end">
          <button
            onClick={() => {
              setTexto('')
              setParams({})
            }}
            className="shrink-0 rounded-full px-3 py-1.5 text-[13px] font-semibold texto-tenue hover:texto-base"
          >
            Limpiar filtros
          </button>
        </div>
      )}

      {lista.length === 0 ? (
        <Vacio
          icono={<Box className="size-7" />}
          titulo="No hay productos que mostrar"
          detalle={
            texto || soloCriticos
              ? 'Prueba con otro término o quita los filtros.'
              : 'Agrega el primer producto para empezar a controlar el stock.'
          }
          accion={
            esAdmin && !texto ? (
              <Boton onClick={() => setNuevo(true)} icono={<Plus className="size-[18px]" />}>
                Nuevo producto
              </Boton>
            ) : undefined
          }
        />
      ) : (
        <div className="flex flex-col gap-2.5 escalonar">
          {lista.map((p) => (
            <FilaProducto key={p.id} producto={p} umbralGlobal={umbral} />
          ))}
        </div>
      )}

      {/* Acciones de admin: al final, para que la lista sea lo primero
          que se vea al entrar. */}
      {esAdmin && (
        <div className="mt-6 flex flex-col gap-2 border-t borde-suave pt-5 sm:flex-row">
          <Link to="/ingreso" className="sm:flex-1">
            <Boton
              variante="secundario"
              icono={<PackagePlus className="size-[18px]" />}
              className="w-full"
            >
              Agregar ingreso
            </Boton>
          </Link>
          <Boton
            icono={<Plus className="size-[18px]" />}
            onClick={() => setNuevo(true)}
            className="w-full sm:flex-1"
          >
            Nuevo producto
          </Boton>
        </div>
      )}

      {/* Orden */}
      <Hoja
        abierta={panelOrden}
        onCerrar={() => setPanelOrden(false)}
        titulo="Ordenar"
        descripcion="Cómo se listan los productos"
        ancho="sm"
      >
        <div className="space-y-1.5 pb-2">
          {ORDENES.map((o) => (
            <button
              key={o.valor}
              onClick={() => {
                setOrden(o.valor)
                setPanelOrden(false)
              }}
              className={cn(
                'flex w-full items-center gap-3 rounded-2xl px-4 py-3.5 text-left text-[15px] font-semibold transition-colors',
                orden === o.valor
                  ? 'bg-bosque-50 text-bosque-600 dark:bg-bosque-500/20 dark:text-bosque-200'
                  : 'texto-base hover:superficie-hundida',
              )}
            >
              <ArrowUpDown className="size-[18px] texto-tenue" />
              {o.etiqueta}
            </button>
          ))}
        </div>
      </Hoja>

      {/* Escáner */}
      <EscanerCodigo
        abierto={escaner}
        onCerrar={() => setEscaner(false)}
        onLeer={(codigo) => {
          setEscaner(false)
          setTexto(codigo)
        }}
      />

      {/* Alta de producto */}
      <FormularioProducto abierto={nuevo} onCerrar={() => setNuevo(false)} />
    </Pagina>
  )
}

// Bordes bien notorios, para que el estado del producto salte a la
// vista sin tener que leer el chip.
const BORDE_AMARILLO_NEON = {
  borderColor: '#ffe000',
  borderWidth: 2,
  boxShadow: '0 0 0 1px rgba(255,224,0,0.35), 0 0 14px 1px rgba(255,224,0,0.7)',
}
const BORDE_ROJO_NEON = {
  borderColor: '#ff1a3c',
  borderWidth: 2,
  boxShadow: '0 0 0 1px rgba(255,26,60,0.35), 0 0 14px 1px rgba(255,26,60,0.65)',
}

export function FilaProducto({
  producto: p,
  umbralGlobal,
}: {
  producto: Producto
  umbralGlobal: number
}) {
  const estado = estadoStock(p, umbralGlobal)
  const meta = ESTADO_STOCK_META[estado]
  const disponible = p.stock - p.reservado
  const pocoStock = p.activo && estado === 'poco'
  // "Sin stock" solo importa remarcarlo en rojo si el producto sigue
  // activo —si ya está descontinuado, la X gris ya lo dice todo.
  const sinStock = estado === 'agotado'
  const sinStockActivo = p.activo && sinStock

  return (
    <Link to={`/inventario/${p.id}`} className="block">
      <Tarjeta
        className="group relative flex cursor-pointer items-center gap-3.5 p-3 transition-all duration-200 ease-[var(--ease-suave)] hover:-translate-y-0.5 hover:sombra-flotante sm:gap-4 sm:p-3.5"
        style={pocoStock ? BORDE_AMARILLO_NEON : sinStockActivo ? BORDE_ROJO_NEON : undefined}
      >
        {/* Descontinuado: una X grande tapa toda la tarjeta. Sigue
            siendo un link —el administrador entra ahí a reactivarlo. */}
        {!p.activo && (
          <div className="absolute inset-0 z-10 grid place-items-center rounded-3xl bg-black/55 backdrop-blur-[1px]">
            <X className="size-12 text-white/90" strokeWidth={2.5} />
          </div>
        )}

        {/* Miniatura: foto si existe, si no la inicial sobre el tono de
            su categoría. Sin stock, se ve en gris. */}
        {p.foto ? (
          <img
            src={p.foto}
            alt=""
            className={cn(
              'size-16 shrink-0 rounded-2xl object-cover sm:size-[72px]',
              sinStock && 'grayscale',
            )}
            loading="lazy"
          />
        ) : (
          <div
            className={cn(
              'grid size-16 shrink-0 place-items-center rounded-2xl font-display text-xl font-bold sm:size-[72px]',
              sinStock
                ? 'bg-neutral-100 text-neutral-400 dark:bg-neutral-800/60 dark:text-neutral-500'
                : TONO_CATEGORIA[p.categoria],
            )}
          >
            {p.nombre.slice(0, 2).toUpperCase()}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <h3 className="truncate text-[15px] leading-snug font-bold texto-base">{p.nombre}</h3>
          <p className="mt-0.5 flex items-center gap-1.5 text-[12px] texto-suave">
            <span className="codigo font-semibold">{p.codigo}</span>
            {sinStock ? (
              <span className="font-semibold text-rojo-500">· Sin stock</span>
            ) : (
              p.peso > 0 && <span>· {pesoLabel(p)}</span>
            )}
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Chip className={meta.chip} punto={meta.punto}>
              {meta.etiqueta}
            </Chip>
            {!p.activo && (
              <Chip className="bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400">
                Descontinuado
              </Chip>
            )}
            {p.reservado > 0 && <Chip>{p.reservado} reservadas</Chip>}
          </div>
        </div>

        {/* La cantidad disponible, siempre visible al costado. */}
        <div className="flex shrink-0 flex-col items-end gap-0.5 pl-1 text-right">
          <p className="cifra font-display text-2xl leading-none font-bold texto-base">
            {num(disponible)}
          </p>
          <p className="text-[11px] font-semibold texto-tenue uppercase">
            {p.unidad === 'unidad' ? 'unidades' : p.unidad}
          </p>
          <p className="cifra text-[12px] font-semibold texto-suave">{money(p.precioSugerido)}</p>
        </div>
        <ChevronRight className="hidden size-4 shrink-0 texto-tenue transition-transform group-hover:translate-x-0.5 sm:block" />
      </Tarjeta>
    </Link>
  )
}
